/**
 * HEAREN V1.1 - useLanguage Hook
 * Centralized language management with reactive updates
 * Listens to languageChanged events and forces re-render
 * V1.3: Persistent language selection across sessions
 */

import { useState, useEffect, useCallback } from 'react';
import translations from '../i18n/translations';

// V1.3: Storage key for persistent language preference
const LANG_STORAGE_KEY = 'hearen_lang';

// V1.3: Normalize language code to "fr" or "en"
const normalizeLanguage = (lang) => {
  if (!lang) return 'fr';
  const normalized = lang.toLowerCase().trim();
  // If starts with "en" (en, en-US, en-GB, etc.) => "en", otherwise "fr"
  return normalized.startsWith('en') ? 'en' : 'fr';
};

// V1.3: Get persisted language (priority: localStorage, fallback: browser language)
const getPersistedLanguage = () => {
  try {
    const stored = localStorage.getItem(LANG_STORAGE_KEY);
    if (stored === 'en' || stored === 'fr') {
      return stored;
    }
    // Migration: check old key
    const oldStored = localStorage.getItem('hearen-language');
    if (oldStored) {
      const normalized = normalizeLanguage(oldStored);
      localStorage.setItem(LANG_STORAGE_KEY, normalized);
      localStorage.removeItem('hearen-language'); // Clean up old key
      return normalized;
    }
    // Fallback to browser language
    const browserLang = navigator.language || navigator.userLanguage || 'fr';
    return normalizeLanguage(browserLang);
  } catch (e) {
    console.warn('[useLanguage] Error reading from localStorage:', e);
    return 'fr';
  }
};

// V1.3: Save language preference
const saveLanguagePreference = (lang) => {
  const normalized = normalizeLanguage(lang);
  try {
    localStorage.setItem(LANG_STORAGE_KEY, normalized);
    console.log(`🌍 [useLanguage] Language preference saved: ${normalized}`);
  } catch (e) {
    console.warn('[useLanguage] Error saving to localStorage:', e);
  }
  return normalized;
};

export const useLanguage = () => {
  // V1.3: Initialize from persisted preference
  const [lang, setLang] = useState(() => getPersistedLanguage());
  
  // Listen for language changes
  useEffect(() => {
    const handleLanguageChange = (event) => {
      const newLang = normalizeLanguage(event.detail?.language || localStorage.getItem(LANG_STORAGE_KEY) || 'fr');
      setLang(newLang);
    };
    
    // Also check on storage events (for cross-tab sync)
    const handleStorageChange = (event) => {
      if (event.key === LANG_STORAGE_KEY) {
        setLang(normalizeLanguage(event.newValue) || 'fr');
      }
    };
    
    window.addEventListener('languageChanged', handleLanguageChange);
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('languageChanged', handleLanguageChange);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);
  
  // Get translations for current language
  const t = translations[lang] || translations.fr;
  
  // V1.3: Change language function with persistence
  const changeLanguage = useCallback((newLang) => {
    const normalized = saveLanguagePreference(newLang);
    setLang(normalized);
    window.dispatchEvent(new CustomEvent('languageChanged', { detail: { language: normalized } }));
  }, []);
  
  return { lang, t, changeLanguage, isFr: lang === 'fr', isEn: lang === 'en' };
};

export default useLanguage;
export { LANG_STORAGE_KEY, normalizeLanguage, getPersistedLanguage, saveLanguagePreference };
